import { Suspense, lazy, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Alert } from '@mui/material';
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

import { ListToolbar } from '../../../list-toolbar/ListToolbar';
import { useLocale } from '../../../src/hooks/locale';
import { lazyRetry } from '../../../src/utils/lazy';
import {
  type Property_Transactions_Bool_Exp,
  type Property_Transactions_Order_By,
} from '../../__generated__/graphql';
import { SmartListTitle } from '../../components/SmartListTitle';
import { TransactionsList } from '../../components/transactions/TransactionsList';
import { useAppData } from '../../providers/AppDataProvider';
import { getPaginationVariables } from '../../utils/paginations';
import { prepareWhereClauseQuery } from '../../utils/parseWhereClause';

import {
  GET_PROPERTY_TRANSACTIONS,
  GET_PROPERTY_TRANSACTIONS_LIST_COUNT,
} from './transactionQueries';
const TransactionCreateDrawer = lazy(() =>
  lazyRetry(() => import('./TransactionCreateDrawer')),
);
const TransactionDrawer = lazy(() =>
  lazyRetry(() => import('./TransactionDrawer')),
);

export const Transactions = () => {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const { me } = useAppData();
  const myTeamsIds = me?.teams?.map(team => team.team_id) ?? [];

  const where: Property_Transactions_Bool_Exp = useMemo(
    () =>
      prepareWhereClauseQuery(JSON.parse(searchParams.get('where') || '{}')),
    [searchParams],
  );

  const order_by: Property_Transactions_Order_By = JSON.parse(
    searchParams.get('order_by') ?? '[{ "signature_date": "desc" }]',
  );

  const { limit, offset } = getPaginationVariables(searchParams);

  const navigate = useNavigate();

  const transactionLink = (id: string) => ({
    pathname: `/transactions/${id}`,
    search: searchParams.toString(),
  });

  const variables = {
    where,
    order_by,
    limit,
    offset,
    team_ids: myTeamsIds,
  };

  const { data, loading, error } = useQuery(GET_PROPERTY_TRANSACTIONS, {
    variables,
  });

  const { data: countData } = useQuery(GET_PROPERTY_TRANSACTIONS_LIST_COUNT, {
    variables: {
      where: variables.where,
    },
  });

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  return (
    <>
      <SmartListTitle
        tableName="property_transactions"
        defaultTitle={t('Transactions')}
      />
      <ListToolbar
        tableName="property_transactions"
        quickFilters={[
          {
            label: t('Signature date'),
            path: ['property_transactions_bool_exp', 'signature_date'],
          },
          {
            label: t('Predicted signature date'),
            path: [
              'property_transactions_bool_exp',
              'estimated_signature_date',
            ],
          },
          {
            label: t('Payment date'),
            path: ['property_transactions_bool_exp', 'payment_date'],
          },
          {
            label: t('Seller agent'),
            path: ['property_transactions_bool_exp', 'seller_broker'],
            filter: {
              path: ['users', 'teams_users'],
              value: {},
            },
          },
          {
            label: t('Seller team'),
            path: ['property_transactions_bool_exp', 'seller_broker_team'],
          },
          {
            label: t('Buyer agent'),
            path: ['property_transactions_bool_exp', 'buyer_broker'],
            filter: {
              path: ['users', 'teams_users'],
              value: {},
            },
          },
          {
            label: t('Buyer team'),
            path: ['property_transactions_bool_exp', 'buyer_broker_team'],
          },
          {
            label: t('Seller'),
            path: [
              'property_transactions_bool_exp',
              'lot',
              'sellers',
              'seller',
            ],
          },
          {
            label: t('Place'),
            path: ['property_transactions_bool_exp', 'property', 'places'],
          },
          {
            label: t('Purchase price'),
            path: ['property_transactions_bool_exp', 'purchase_price'],
          },
          {
            label: t('Source'),
            path: [
              'property_transactions_bool_exp',
              'lot',
              'origin',
              'id',
              '_in',
            ],
            displayedColumn: 'label',
            filter: {
              path: ['name', 'type', '_in'],
              value: 'lot_origin_types',
            },
          },
        ]}
        newLink={{
          pathname: 'new',
          search: searchParams.toString(),
        }}
      />
      <TransactionsList
        transactions={data?.property_transactions}
        loading={loading}
        displayPropertyInfo
        showReviews
        extraDataGridProps={{
          rowCount:
            countData?.property_transactions_aggregate?.aggregate?.count ?? 0,
          onRowClick: ({ id }, e) => {
            if (id === 'auto-generated-group-footer-root') {
              return;
            } else if (id != null) {
              if (e.metaKey || e.ctrlKey) {
                window.open(transactionLink(id as string).pathname, '_blank');
                return;
              }
              navigate(transactionLink(id as string));
            }
          },
          initialState: {
            aggregation: {
              model: {
                purchase_price: 'sum',
                'commissions_aggregate.sum.amount': 'sum',
                'my_commissions.aggregate.sum.amount': 'sum',
              },
            },
          },
        }}
      />
      <Suspense fallback={null}>
        <Routes>
          <Route
            path="new/*"
            element={
              <TransactionCreateDrawer
                onCreate={(id: string) => {
                  navigate({
                    pathname: `${id}/transaction`,
                    search: searchParams.toString(),
                  });
                }}
                refetchQueries={[
                  {
                    query: GET_PROPERTY_TRANSACTIONS,
                    variables,
                  },
                  {
                    query: GET_PROPERTY_TRANSACTIONS_LIST_COUNT,
                    variables: { where: variables.where },
                  },
                ]}
              />
            }
          />
          <Route
            path=":transactionId/*"
            element={
              <TransactionDrawer
                refetchQueries={[
                  { query: GET_PROPERTY_TRANSACTIONS, variables },
                ]}
              />
            }
          />
        </Routes>
      </Suspense>
    </>
  );
};

export default Transactions;
